import React, { useState } from 'react';
import YachanguBookingPage from './YachanguBookingPage';
import YachanguRidesPage from './YachanguRidesPage';
import YachanguSettingsPage from './YachanguSettingsPage';
import YachanguPersonalInfoPage from './YachanguPersonalInfoPage';
import YachanguFavoritesPage from './YachanguFavoritesPage';
import HamburgerMenu from './HamburgerMenu';
import BottomNavigation from './BottomNavigation';
import DriverAllocationModal from './DriverAllocationModal';
import LiveRideTracking from './LiveRideTracking';
import TripPaymentModal from './TripPaymentModal';
import NotificationBell from './NotificationBell';
import { createRide } from '../../services/rideService';
import { authStorage } from '../../utils/storage';
import { toast } from '../../hooks/use-toast';
import CountryRestrictionBanner from '../shared/CountryRestrictionBanner';
import { useCountry } from '../../contexts/CountryContext';

const YachanguStyleDashboard = ({ user, onLogout }) => {
  const ctx = useCountry();
  const bookingBlocked = ctx && !ctx.loading && ctx.isLive && !ctx.isSupportedCountry;
  const [activeTab, setActiveTab] = useState('home');
  const [showMenu, setShowMenu] = useState(false);
  const [showPersonalInfo, setShowPersonalInfo] = useState(false);
  const [currentRide, setCurrentRide] = useState(null);
  const [showDriverAllocationModal, setShowDriverAllocationModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [completedRideDetails, setCompletedRideDetails] = useState(null);
  // When set, the settings page deep-links into a sub-page (e.g. 'family') and
  // optionally a sub-tab inside that page (e.g. 'approvals')
  const [settingsDeepLink, setSettingsDeepLink] = useState(null);

  const goToFamilyApprovals = () => {
    setSettingsDeepLink({ page: 'family', tab: 'approvals' });
    setActiveTab('account');
  };

  const handleConfirmRide = async (rideData) => {
    if (bookingBlocked) {
      toast({
        title: 'Not available in your country yet',
        description: 'Booking is disabled in your region. You can still explore the app.',
        variant: 'destructive'
      });
      return;
    }
    try {
      // Get rider ID from auth
      const authData = await authStorage.getAuth();
      if (!authData || !authData.user) {
        toast({ 
          title: 'Error', 
          description: 'Please login to book a ride', 
          variant: 'destructive' 
        });
        return;
      }

      const riderId = authData.user.id;

      // Create ride request
      const rideRequest = {
        rider_id: riderId,
        vehicle_type: rideData.vehicleType,
        pickup_location: {
          latitude: rideData.pickup.lat,
          longitude: rideData.pickup.lng,
          address: 'Current Location'
        },
        dropoff_location: {
          latitude: rideData.destination.lat,
          longitude: rideData.destination.lng,
          address: rideData.destination.address
        },
        estimated_price: rideData.estimatedPrice,
        distance: rideData.distance || null,
        payment_method: rideData.paymentMethod || 'mobile_money',
        apply_family_credit: !!rideData.applyFamilyCredit,
        apply_personal_credit: !!rideData.applyPersonalCredit,
        // Add scheduled time if present
        ...(rideData.scheduledTime && {
          scheduled_time: rideData.scheduledTime.toISOString(),
          status: 'scheduled'
        })
      };

      console.log('📤 Creating ride:', rideRequest);

      const result = await createRide(rideRequest);

      if (result.success) {
        // Family approval flow: ride is held until guardian approves
        if (result.data?.awaiting_approval) {
          toast({
            title: 'Awaiting guardian approval',
            description: result.data?.message || 'Your guardian needs to approve this ride before drivers are notified.',
          });
          // Don't show driver-finding modal; reset booking state instead
          return;
        }

        // Scheduled rides are NOT dispatched now — they must land in "Upcoming"
        // and be picked up by the scheduler near the pickup time. Do NOT open the
        // driver-allocation modal (it would expire the ride after its 2-min timeout).
        if (rideData.scheduledTime) {
          const when = rideData.scheduledTime.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
          toast({
            title: 'Ride scheduled ✓',
            description: `We'll match you with a driver near ${when}. Find it under History → Upcoming.`,
          });
          return;
        }

        // Store ride details
        const rideDetails = {
          ...result.data,
          pickup: 'Current Location',
          dropoff: rideData.destination.address,
          vehicle_type: rideData.vehicleType,
          estimated_price: rideData.estimatedPrice,
          distance: rideData.distance ?? result.data?.distance,
          pickupCoords: rideData.pickup,
          dropoffCoords: rideData.destination
        };

        setCurrentRide(rideDetails);
        setShowDriverAllocationModal(true);

        toast({ 
          title: 'Finding nearby drivers...', 
          description: 'Please wait' 
        });
      } else {
        toast({ 
          title: 'Error', 
          description: result.message, 
          variant: 'destructive' 
        });
      }
    } catch (error) {
      console.error('Error creating ride:', error);
      toast({ 
        title: 'Error', 
        description: 'Failed to create ride request', 
        variant: 'destructive' 
      });
    }
  };

  const handleDriverAccepted = (rideWithDriver) => {
    setShowDriverAllocationModal(false);
    setCurrentRide({
      ...currentRide,
      driver_id: rideWithDriver.driver_id,
      driver: rideWithDriver.driver,
      status: rideWithDriver.status
    });
  };

  const handleCancelRide = () => {
    setShowDriverAllocationModal(false);
    setCurrentRide(null);
    toast({ 
      title: 'Ride Cancelled', 
      description: 'Your ride request has been cancelled' 
    });
  };

  const handleTripCompleted = (rideDetails) => {
    setCompletedRideDetails(rideDetails);
    setShowPaymentModal(true);
  };

  const handlePaymentComplete = () => {
    setShowPaymentModal(false);
    setCurrentRide(null);
    setCompletedRideDetails(null);
    toast({
      title: 'Trip Completed!',
      description: 'Thank you for riding with Ride Yachangu'
    });
  };

  // Render active ride tracking
  if (currentRide && currentRide.driver) {
    return (
      <>
        <LiveRideTracking
          rideDetails={currentRide}
          driverDetails={currentRide.driver}
          onCancelRide={handleCancelRide}
          onTripCompleted={handleTripCompleted}
        />
        <TripPaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          rideDetails={completedRideDetails}
          finalFare={completedRideDetails?.estimated_price || 0}
          onPaymentComplete={handlePaymentComplete}
        />
      </>
    );
  }

  return (
    <div className="relative h-screen overflow-hidden bg-white dark:bg-gray-900 transition-colors">
      {/* Notification Bell - below top bar */}
      <div className="absolute top-20 right-4 z-[60]">
        <NotificationBell userId={user?.id} onFamilyApprovalsClick={goToFamilyApprovals} />
      </div>

      {/* Main Content based on active tab */}
      {activeTab === 'home' && (
        <>
          {bookingBlocked && (
            <div className="absolute top-16 left-0 right-0 z-[55]">
              <CountryRestrictionBanner user={user} />
            </div>
          )}
          <YachanguBookingPage
            user={user}
            onMenuClick={() => setShowMenu(true)}
            onConfirmRide={handleConfirmRide}
          />
        </>
      )}

      {activeTab === 'favorites' && (
        <YachanguFavoritesPage 
          onSelectDestination={(destination) => {
            // Switch to home tab and trigger booking with selected destination
            setActiveTab('home');
            // TODO: Pass destination to booking page
          }}
        />
      )}

      {activeTab === 'rides' && <YachanguRidesPage />}

      {activeTab === 'account' && !showPersonalInfo && (
        <YachanguSettingsPage
          user={user}
          onNavigateToPersonalInfo={() => setShowPersonalInfo(true)}
          onClose={() => { setSettingsDeepLink(null); setActiveTab('home'); }}
          deepLink={settingsDeepLink}
          onDeepLinkConsumed={() => setSettingsDeepLink(null)}
        />
      )}

      {activeTab === 'account' && showPersonalInfo && (
        <YachanguPersonalInfoPage
          user={user}
          onLogout={onLogout}
          onBack={() => setShowPersonalInfo(false)}
        />
      )}

      {/* Bottom Navigation */}
      <BottomNavigation 
        activeTab={activeTab} 
        onTabChange={setActiveTab} 
      />

      {/* Side Menu Drawer */}
      {showMenu && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setShowMenu(false)}
          ></div>

          {/* Menu Panel */}
          <div className="fixed top-0 left-0 bottom-0 w-80 bg-white z-50 shadow-2xl overflow-hidden">
            <HamburgerMenu
              user={user}
              onClose={() => setShowMenu(false)}
            />
          </div>
        </>
      )}

      {/* Driver Allocation Modal */}
      <DriverAllocationModal
        isOpen={showDriverAllocationModal}
        onClose={() => setShowDriverAllocationModal(false)}
        rideDetails={currentRide}
        onCancelRide={handleCancelRide}
        onDriverAccepted={handleDriverAccepted}
      />
    </div>
  );
};

export default YachanguStyleDashboard;
